import { formatDate, formatTime } from '@smarttransit/common'
import { addAlert, initBreadcrumbs, isAuthorized } from '@/utilities/helpers'
import { genericApiRequests } from '@/utilities/axios-factory'
import {
  findOcppChargers,
  findTotalOcppChargers,
  deleteOcppCharger
} from '@/services/ocpp-charger-service'
import OcppEmulatorDialog from './components/ocpp-emulator-dialog'
import FleetChargingPanel from './components/fleet-charging-panel'
import FleetIdTagsPanel from './components/fleet-id-tags-panel'

const MAP_LAYER_ID = 'evChargerLayer'
const MAP_HIT_LAYER_ID = 'evChargerHitLayer'
const MAP_SOURCE_ID = 'evChargerSource'
const MAP_IMAGE_ID = 'ev-charger-marker'
// 128px source image; ~0.28 renders near 36px (st-map HTML pins are 40px)
const MAP_MARKER_ICON_SIZE = 0.28
const TAB_LIST = 0
const TAB_MAP = 1
const TAB_ASSIGNMENTS = 2
const TAB_ID_TAGS = 3

function mapPopupHtml (charger) {
  return `
    <h4>${charger.chargePointId || '-'}</h4>
    <div>Status: ${charger.status || '-'}</div>
    <div>Model: ${charger.model || '-'}</div>
    <div><small>${charger.locationLabel || '-'}</small></div>
  `
}

export default {
  name: 'advanced-ev-chargers',
  components: {
    OcppEmulatorDialog,
    FleetChargingPanel,
    FleetIdTagsPanel
  },
  props: {
    signedInUser: Object
  },
  data () {
    return {
      breadcrumbs: [],
      apiInProgress: false,
      mapApiInProgress: false,
      activeTab: TAB_LIST,
      searchKeywords: '',
      records: [],
      mapRecords: [],
      totalRecords: 0,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateUpdated'
      },
      headers: [
        { text: 'Charge Point ID', align: 'left', sortable: true, value: 'chargePointId' },
        { text: 'Status', align: 'left', sortable: true, value: 'status' },
        { text: 'Model', align: 'left', sortable: true, value: 'model' },
        { text: 'Location', align: 'left', sortable: true, value: 'locationLabel' },
        { text: 'Last Seen', align: 'left', sortable: true, value: 'lastSeenAt' },
        { text: 'Credential', align: 'left', sortable: false, value: 'hasAuthToken' },
        { text: 'Actions', align: 'right', sortable: false, value: 'actions' }
      ],
      stMapApis: null,
      mapboxAccessToken: process.env.VUE_APP_MAPBOX_KEY,
      maptilerKey: process.env.VUE_APP_MAPTILER_KEY,
      mapBounds: null,
      mapShouldRender: false,
      mapMarkerEventsRegistered: false,
      refreshMapRecordsTimeoutHandle: null,
      emulatorDialogVisible: false,
      selectedEmulatorChargePointId: null
    }
  },
  computed: {
    isAdmin () {
      return isAuthorized(this.signedInUser, 'admin')
    },
    isStagingOrLocal () {
      return process.env.VUE_APP_ENV !== 'production'
    },
    canUseEmulator () {
      return this.isAdmin && this.isStagingOrLocal
    }
  },
  watch: {
    $route () {
      this.breadcrumbs = initBreadcrumbs.bind(this)()
      if (!this.isListRouteActive()) {
        this.blurFleetChargingPanel()
        return
      }

      const routeTab = this.getTabFromRoute()
      if (Number(this.activeTab) !== routeTab) {
        this.activeTab = routeTab
      }

      if (this._skipNextRouteRefresh && this.$route.query && this.$route.query.tab === this.getTabQueryValue(this.activeTab)) {
        this._skipNextRouteRefresh = false
        return
      }

      this.refreshActiveTab()
    }
  },
  mounted () {
    this._mapboxInstanceWrapper = null
    this._mapPopup = null
    this._skipNextRouteRefresh = false
    this.syncActiveTabFromRoute()

    this.stMapApis = {
      genericApiRequests,
      addAlert
    }

    this.$nextTick(() => {
      this.breadcrumbs = initBreadcrumbs.bind(this)()
      if (!this.isListRouteActive()) {
        return
      }
      this.refreshActiveTab()
    })
  },
  methods: {
    syncActiveTabFromRoute () {
      this.activeTab = this.getTabFromRoute()
      if (this.isTabActive(TAB_MAP)) {
        this.mapShouldRender = true
      }
    },
    getTabFromRoute () {
      if (this.$route && this.$route.query && this.$route.query.tab === 'map') {
        return TAB_MAP
      }
      if (this.$route && this.$route.query && this.$route.query.tab === 'assignments') {
        return TAB_ASSIGNMENTS
      }
      if (this.isAdmin && this.$route && this.$route.query && this.$route.query.tab === 'id-tags') {
        return TAB_ID_TAGS
      }
      return TAB_LIST
    },
    getTabQueryValue (tab) {
      if (Number(tab) === TAB_MAP) {
        return 'map'
      }
      if (Number(tab) === TAB_ASSIGNMENTS) {
        return 'assignments'
      }
      if (Number(tab) === TAB_ID_TAGS) {
        return 'id-tags'
      }
      return 'list'
    },
    updateTabQuery (tab) {
      if (!this.$route || !this.isListRouteActive()) {
        return
      }

      const tabValue = this.getTabQueryValue(tab)
      if (this.$route.query && this.$route.query.tab === tabValue) {
        return
      }

      const query = Object.assign({}, this.$route.query || {}, { tab: tabValue })
      this._skipNextRouteRefresh = true
      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query
      }).catch(() => {
        this._skipNextRouteRefresh = false
      })
    },
    refreshActiveTab () {
      if (!this.isTabActive(TAB_ASSIGNMENTS)) {
        this.blurFleetChargingPanel()
      }

      if (this.isTabActive(TAB_LIST)) {
        this.searchRecords()
        return
      }

      if (this.isTabActive(TAB_ASSIGNMENTS)) {
        this.focusFleetChargingPanel()
        return
      }

      if (this.isTabActive(TAB_ID_TAGS)) {
        this.focusFleetIdTagsPanel()
        return
      }

      this.mapShouldRender = true
      this.resizeMapWhenVisible(() => {
        this.refreshMapRecords()
      })
    },
    onTabChanged (value) {
      if (!this.isListRouteActive()) {
        return
      }

      const activeTab = Number(value)
      this.activeTab = activeTab
      this.updateTabQuery(activeTab)

      this.refreshActiveTab()
    },
    focusFleetChargingPanel () {
      this.$nextTick(() => {
        const panel = this.$refs.fleetChargingPanel
        if (panel && typeof panel.focusPanel === 'function') {
          panel.focusPanel()
        } else if (panel && typeof panel.focusMap === 'function') {
          panel.focusMap()
        }
      })
    },
    blurFleetChargingPanel () {
      const panel = this.$refs.fleetChargingPanel
      if (panel && typeof panel.blurPanel === 'function') {
        panel.blurPanel()
      }
    },
    focusFleetIdTagsPanel () {
      this.$nextTick(() => {
        const panel = this.$refs.fleetIdTagsPanel
        if (panel && typeof panel.focusPanel === 'function') {
          panel.focusPanel()
        }
      })
    },
    isTabActive (value) {
      return Number(this.activeTab) === value
    },
    isListRouteActive () {
      return this.$route && this.$route.name === 'advanced-ev-chargers'
    },
    buildOrder () {
      const sortBy = this.pagination.sortBy || 'dateUpdated'
      const sortDirection = this.pagination.descending ? 'DESC' : 'ASC'
      return sortBy + ' ' + sortDirection
    },
    async searchRecords () {
      if (!this.isListRouteActive()) {
        return
      }

      this.apiInProgress = true
      try {
        const page = this.pagination.page || 1
        const rowsPerPage = this.pagination.rowsPerPage || 10
        const offset = (page - 1) * rowsPerPage
        const search = this.searchKeywords || undefined
        const order = this.buildOrder()

        const result = await Promise.all([
          findOcppChargers({
            limit: rowsPerPage,
            offset,
            order,
            search
          }),
          findTotalOcppChargers({ search })
        ])

        this.records = result[0] || []
        this.totalRecords = result[1] && result[1].count ? Number(result[1].count) : 0
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to load chargers',
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async refreshMapRecords () {
      if (!this.isListRouteActive()) {
        return
      }
      const bounds = this.getCurrentMapBounds()
      if (!bounds) {
        return
      }

      this.mapApiInProgress = true
      try {
        const search = this.searchKeywords || undefined
        this.mapRecords = await findOcppChargers({
          limit: 500,
          offset: 0,
          order: 'dateUpdated DESC',
          search,
          bounds
        })
        await this.renderMapMarkers()
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to load charger map data',
          type: 'error'
        })
      } finally {
        this.mapApiInProgress = false
      }
    },
    formatDateTime (value, timezone) {
      if (!value) {
        return '-'
      }

      const trimmedTimezone = timezone && String(timezone).trim()
      return `${formatTime(value, trimmedTimezone)}, ${formatDate(value, trimmedTimezone, { displayTimezone: true })}`
    },
    async onApplySearch () {
      this.pagination.page = 1
      if (this.isTabActive(TAB_LIST)) {
        await this.searchRecords()
      } else {
        await this.refreshMapRecords()
      }
    },
    async onPagination () {
      if (this.isTabActive(TAB_LIST)) {
        await this.searchRecords()
      }
    },
    openRecord (item) {
      if (!item || !item.id) {
        return
      }
      this.$router.push({
        name: 'advanced-ev-chargers-charger',
        params: { chargerId: item.id }
      })
    },
    openEmulatorDialog (item) {
      if (!this.canUseEmulator || !item || !item.chargePointId) {
        return
      }
      this.selectedEmulatorChargePointId = item.chargePointId
      this.emulatorDialogVisible = true
    },
    async deleteRecord (item) {
      if (!item || !item.id) {
        return
      }

      if (!confirm('Confirm deleting charger ' + (item.chargePointId || item.id) + '?')) {
        return
      }

      try {
        await deleteOcppCharger({ id: item.id })
        addAlert({
          message: 'Charger deleted',
          type: 'success'
        })
        await this.searchRecords()
        await this.refreshMapRecords()
      } catch (err) {
        addAlert({
          message: err && err.message ? err.message : 'Unable to delete charger',
          type: 'error'
        })
      }
    },
    onMapLoaded (mapboxInstance) {
      this._mapboxInstanceWrapper = Object.freeze({ mapboxInstance })
      this.mapMarkerEventsRegistered = false
      this.closeMapPopup()
      if (this.isTabActive(TAB_MAP)) {
        this.resizeMapWhenVisible(() => {
          this.refreshMapRecords()
        })
      }
    },
    getMapboxInstance () {
      return this._mapboxInstanceWrapper ? this._mapboxInstanceWrapper.mapboxInstance : null
    },
    onMapBoundsChanged (data) {
      const bounds = this.normalizeMapBounds(data && data.bounds)
      if (bounds) {
        this.mapBounds = bounds
      }
      if (!this.isTabActive(TAB_MAP)) {
        return
      }
      clearTimeout(this.refreshMapRecordsTimeoutHandle)
      this.refreshMapRecordsTimeoutHandle = setTimeout(() => {
        this.refreshMapRecords()
      }, 400)
    },
    normalizeMapBounds (bounds) {
      if (!bounds) {
        return null
      }

      if (typeof bounds.getWest === 'function') {
        return {
          west: bounds.getWest(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          north: bounds.getNorth()
        }
      }

      if (bounds._sw && bounds._ne) {
        return {
          west: bounds._sw.lng,
          south: bounds._sw.lat,
          east: bounds._ne.lng,
          north: bounds._ne.lat
        }
      }

      if (
        typeof bounds.west !== 'undefined' &&
        typeof bounds.south !== 'undefined' &&
        typeof bounds.east !== 'undefined' &&
        typeof bounds.north !== 'undefined'
      ) {
        return {
          west: Number(bounds.west),
          south: Number(bounds.south),
          east: Number(bounds.east),
          north: Number(bounds.north)
        }
      }

      return null
    },
    getCurrentMapBounds () {
      const mapboxInstance = this.getMapboxInstance()
      if (mapboxInstance && mapboxInstance.currentMap && typeof mapboxInstance.currentMap.getBounds === 'function') {
        const bounds = this.normalizeMapBounds(mapboxInstance.currentMap.getBounds())
        if (bounds) {
          this.mapBounds = bounds
          return bounds
        }
      }
      return this.mapBounds
    },
    resizeMapWhenVisible (callback) {
      this.$nextTick(() => {
        setTimeout(() => {
          const mapboxInstance = this.getMapboxInstance()
          window.dispatchEvent(new Event('resize'))
          if (mapboxInstance && mapboxInstance.currentMap && typeof mapboxInstance.currentMap.resize === 'function') {
            mapboxInstance.currentMap.resize()
          }
          if (typeof callback === 'function') {
            callback()
          }
        }, 100)
      })
    },
    closeMapPopup () {
      if (this._mapPopup) {
        this._mapPopup.remove()
        this._mapPopup = null
      }
    },
    showMapPopup (feature, options) {
      const mapboxInstance = this.getMapboxInstance()
      if (!mapboxInstance || !mapboxInstance.currentMap || !feature || !feature.properties || !feature.geometry) {
        return
      }
      options = options || {}

      this.closeMapPopup()
      this._mapPopup = mapboxInstance.createPopup({
        className: 'st-map--popup',
        closeButton: !!options.closeButton,
        closeOnClick: !!options.closeOnClick,
        offset: 12
      })
      this._mapPopup.on('close', () => {
        this._mapPopup = null
      })
      this._mapPopup
        .setLngLat(feature.geometry.coordinates.slice())
        .setHTML(mapPopupHtml(feature.properties))
        .addTo(mapboxInstance.currentMap)
    },
    cloneMapFeature (feature) {
      if (!feature) {
        return null
      }
      return JSON.parse(JSON.stringify({
        geometry: {
          type: feature.geometry && feature.geometry.type,
          coordinates: feature.geometry && feature.geometry.coordinates ? feature.geometry.coordinates.slice() : []
        },
        properties: Object.assign({}, feature.properties || {})
      }))
    },
    async renderMapMarkers () {
      const mapboxInstance = this.getMapboxInstance()
      if (!mapboxInstance || !mapboxInstance.currentMap) {
        return
      }

      const pointsData = (this.mapRecords || [])
        .filter((record) => {
          return typeof record.longitude === 'number' && !isNaN(record.longitude) &&
            typeof record.latitude === 'number' && !isNaN(record.latitude)
        })
        .map((record) => {
          const longitude = Number(record.longitude)
          const latitude = Number(record.latitude)
          return {
            id: record.id,
            chargePointId: record.chargePointId || '',
            status: record.status || '',
            vendor: record.vendor || '',
            model: record.model || '',
            locationLabel: record.locationLabel || '',
            hasAuthToken: !!record.hasAuthToken,
            longitude,
            latitude,
            lng: longitude,
            lat: latitude
          }
        })

      const sourceData = {
        type: 'FeatureCollection',
        features: pointsData.map((point) => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [point.lng, point.lat]
            },
            properties: Object.assign({}, point)
          }
        })
      }
      const currentMap = mapboxInstance.currentMap

      if (!currentMap.hasImage(MAP_IMAGE_ID)) {
        await mapboxInstance.addLayerImage(`${location.origin}/images/ev-charger-marker.png`, MAP_IMAGE_ID)
      }

      if (currentMap.getSource(MAP_SOURCE_ID)) {
        currentMap.getSource(MAP_SOURCE_ID).setData(sourceData)
      } else {
        currentMap.addSource(MAP_SOURCE_ID, {
          type: 'geojson',
          data: sourceData
        })
      }

      if (!currentMap.getLayer(MAP_LAYER_ID)) {
        currentMap.addLayer({
          id: MAP_LAYER_ID,
          type: 'symbol',
          source: MAP_SOURCE_ID,
          layout: {
            'icon-image': MAP_IMAGE_ID,
            'icon-size': MAP_MARKER_ICON_SIZE,
            'icon-anchor': 'center',
            'icon-allow-overlap': true
          }
        })
      } else {
        currentMap.setLayoutProperty(MAP_LAYER_ID, 'icon-size', MAP_MARKER_ICON_SIZE)
        currentMap.setLayoutProperty(MAP_LAYER_ID, 'icon-anchor', 'center')
      }

      if (!currentMap.getLayer(MAP_HIT_LAYER_ID)) {
        currentMap.addLayer({
          id: MAP_HIT_LAYER_ID,
          type: 'circle',
          source: MAP_SOURCE_ID,
          paint: {
            'circle-radius': 22,
            'circle-color': '#204da2',
            'circle-opacity': 0.01
          }
        })
      }

      this.registerMapMarkerEvents()
    },
    registerMapMarkerEvents () {
      const mapboxInstance = this.getMapboxInstance()
      if (this.mapMarkerEventsRegistered || !mapboxInstance || !mapboxInstance.currentMap) {
        return
      }

      const currentMap = mapboxInstance.currentMap

      currentMap.on('click', MAP_HIT_LAYER_ID, (e) => {
        const feature = this.cloneMapFeature(e.features && e.features.length ? e.features[0] : null)
        if (!feature || !feature.properties) {
          return
        }
        this.openRecord({ id: feature.properties.id })
      })

      currentMap.on('mouseenter', MAP_HIT_LAYER_ID, (e) => {
        const feature = this.cloneMapFeature(e.features && e.features.length ? e.features[0] : null)
        if (!feature || !feature.properties) {
          return
        }

        currentMap.getCanvas().style.cursor = 'pointer'
        this.showMapPopup(feature, { closeButton: false, closeOnClick: false })
      })

      currentMap.on('mouseleave', MAP_HIT_LAYER_ID, () => {
        currentMap.getCanvas().style.cursor = ''
        this.closeMapPopup()
      })

      this.mapMarkerEventsRegistered = true
    }
  },
  beforeDestroy () {
    this.blurFleetChargingPanel()
    this.closeMapPopup()
    this.mapMarkerEventsRegistered = false
    clearTimeout(this.refreshMapRecordsTimeoutHandle)
  }
}
